import { Fragment, useEffect, useRef, useState } from 'react';
import { Button, Layout, MessagePlugin } from 'tdesign-react';

import variable from '@/assets/style/variable.module.less';
import { FormComponent, IFormItemProps } from '@/components';
import { clip, clipCustom } from '@/components/render/common';
import SvgIcon from '@/components/tools/svg-icon';
import { RichMediaValue } from '@/interface/media';
import UploadPic from '@/material/form-upload/image';
import { baseRender, customRender } from '@/material/render';

import svgConfig from '../../dev/generateTemplate/sources/svgJson.json';
import { Model3DViewer, Model3DViewerRef } from './3dViewer';
import { formDemos } from './index.config';
import styles from './index.module.less';
import { exportToPSD } from './psd';

const { Aside, Content } = Layout;

const initFormProps = (key: string) => {
  const retData = {
    props: key,
    render: key,
    label: key,
    uiOptions: {
    },
  } as any;
  if (['radio', 'select', 'checkbox'].includes(key)) {
    retData.uiOptions.options = [
      { label: '选项一', value: 1 },
      { label: '选项二', value: 2 },
    ];
  }
  return retData;
};

const UiLayout = () => {
  const [styleArr, setStyleArr] = useState<{ [key: string]: string }[]>([]);
  const formConfig = Object.keys(baseRender).map(key => (initFormProps(key) satisfies IFormItemProps));
  const customConfig = formDemos;


  useEffect(() => {
    initVariable();
  }, []);

  const initVariable = () => {
    const { variableName } = variable;
    const variableArr = variableName.split(' ');
    const darkDom = document.querySelector('#root') as Element;
    const styles = window.getComputedStyle(darkDom);
    const arr = variableArr.map((key) => {
      const value = styles.getPropertyValue(key);
      const type = /color/.test(key) ? 'color' : 'other';
      return {
        key,
        value,
        type,
      };
    });
    const colors = globalColor();
    setStyleArr([...arr, ...colors]);
  };

  const globalColor = () => {
    const elements = document.querySelectorAll('*');
    const colorCounts: string[] = [];

    elements.forEach((el) => {
      const { color } = window.getComputedStyle(el);
      colorCounts.push(color as string);
    });

    const colors = Array.from(new Set(colorCounts)).map(e => ({ value: e, key: e, type: 'color' }));

    return colors;
  };


  const getValueDom = (type: 'color' | 'other', value: string) => {
    if (type === 'color') {
      return <div className={styles['col-value']} style={{ background: value }}></div>;
    }
    return <div className={styles['other-value']}>{value}</div>;
  };

  const getStyleVariable = (type: 'color' | 'other') => {
    const styleDom = styleArr.filter(item => item.type === type).map((item, index) => <div className={styles['var-wrap']} key={index}>
      <div className={styles['var-key']}>
        {clip(item.key)}</div>
      {getValueDom(type, item.value)}
    </div>);
    return styleDom;
  };

  const getIconDom = ({ name, path, size = 24 }: any, key: number) => {
    const clipText = `<SvgIcon name="${path}" width="${size}" height="${size}" />`;
    return <div key={key} className={styles['ex-icon']}>
      <SvgIcon name={path} width={size} height={size} />
      <div className="nova-text-info">
        {clipCustom(name, clipText)}
      </div>
    </div>;
  };

  const modelViewerRef = useRef<Model3DViewerRef>(null);
  const [bgImage, setBgImage] = useState<RichMediaValue>();

  const handleExport = async () => {
    try {
      if (!bgImage?.url?.url) {
        MessagePlugin.error('请先上传背景图');
        return;
      }

      if (!modelViewerRef.current) {
        MessagePlugin.error('3D模型未初始化');
        return;
      }

      // 1. 获取3D模型截图
      const modelBlob = await modelViewerRef.current.captureScreenshot();

      // 2. 导出PSD
      await exportToPSD(bgImage.url.url, modelBlob);

      MessagePlugin.success('PSD文件导出成功');
    } catch (error) {
      console.error('导出失败:', error);
    }
  };

  return (
    (
      <Fragment>
        <Layout>
          <Aside width='420px'>
            <div id="dark" className={styles['ex-wrapper']}>
              <div className={styles.title}>颜色</div>
              <div className={styles.pannel}>{getStyleVariable('color')}</div>
              <div className={styles.title}>其他</div>
              <div className={styles.pannel}>{getStyleVariable('other')}</div>
            </div>
          </Aside>

          <Aside width='500px'>
            <div className={styles['ex-wrapper']}>
              <div className={styles.title}>Icon图标</div>
              <div className={styles.pannel}>
                {svgConfig.map((icon, index) => getIconDom(icon, index))}
              </div>
            </div>
          </Aside>
          <Content style={{ width: '600px' }}>
            <div className={styles['ex-wrapper']}>
              <div className={styles.title}>基础表单</div>
              <div className={styles.pannel}>
                <FormComponent
                  formConfig={{
                    labelAlign: 'top',
                  }}
                  fieldsConfig={formConfig}
                  customRender={baseRender}
                />
              </div>
            </div>
          </Content>

          <Content style={{ width: '600px' }}>
            <div className={styles['ex-wrapper']}>
              <div className={styles.title}>自定义表单</div>
              <div className={styles.pannel}>
                <FormComponent
                  formConfig={{
                    labelAlign: 'top',
                    onValuesChange: (content: any) => {
                      console.log('form config', content);
                    },
                  }}
                  fieldsConfig={customConfig}
                  customRender={customRender}
                />
              </div>
            </div>
          </Content>

          <Content style={{ width: '800px' }}>
            <div className={styles['ex-wrapper']}>
              <div className={styles.title}>3D模型与PS导出</div>

              {/* 背景图上传 */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                  上传背景图:
                </div>
                <UploadPic
                  value={bgImage || {}}
                  onChange={setBgImage}
                  width={200}
                  height={200}
                />
              </div>

              {/* 3D模型查看器 */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                  3D模型:
                </div>
                <Model3DViewer
                  ref={modelViewerRef}
                  bgImage={bgImage?.url?.url}
                />
              </div>

              {/* 导出按钮 */}
              <div style={{ marginTop: '20px' }}>
                <Button
                  onClick={handleExport}
                >
                  导出PSD文件
                </Button>
              </div>
            </div>
          </Content>

        </Layout>
      </Fragment>
    )
  );
};

export default UiLayout;
